import { formatarDuracao } from '@projetive-op/domain'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useSubscription } from '@trpc/tanstack-react-query'
import { CloudOff, Factory, TriangleAlert } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { MarcaOP } from '@/components/marca'
import { EsqueletoCartoes, EstadoVazio } from '@/components/ui/estado'
import { useRelogio } from '@/hooks/use-relogio'
import { useTelaLigada } from '@/hooks/use-tela-ligada'
import { cn } from '@/lib/cn'
import { decorridoSeg, estourou } from '@/lib/postos'
import { lembrarArea } from '@/lib/pwa'
import { PERFIS_WEB, exigirSessao } from '@/lib/sessao'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/painel')({
  // `/painel?segundos=20` ajusta o tempo de cada linha no loop (padrão 12 s).
  validateSearch: z.object({ segundos: z.coerce.number().int().min(3).max(120).optional() }),
  // A TV precisa de uma sessão da gestão ou da diretoria (o operador não vê os
  // colegas). Sem ela, vai ao login e volta para cá.
  beforeLoad: async ({ context, location }) => {
    await exigirSessao({ context, location, perfis: PERFIS_WEB })
  },
  component: Painel,
})

const SEGUNDOS_POR_LINHA_PADRAO = 12

type Posto = NonNullable<ReturnType<typeof usePainelAgora>['data']>[number]

function usePainelAgora() {
  const trpc = useTRPC()
  // Visão coletiva: sem nomes, salvo opt-in da empresa (cláusula 8.4).
  return useQuery({
    ...trpc.painel.agora.queryOptions({ coletivo: true }),
    refetchInterval: 60_000,
  })
}

/**
 * TV da fábrica. Mostra POSTOS, não pessoas (cláusula 8.4 / decisão do
 * kickoff): o que está rodando, há quanto tempo, e se está no ritmo.
 * Quando as linhas não cabem na tela, mostra uma por vez em loop (RNF-02).
 */
function Painel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { segundos = SEGUNDOS_POR_LINHA_PADRAO } = Route.useSearch()
  const agora = usePainelAgora()
  const relogio = useRelogio()
  // TV ligada o dia todo: a tela não pode apagar; o app instalado abre direto no painel.
  useTelaLigada()
  useEffect(() => lembrarArea('/painel'), [])

  useSubscription(
    trpc.painel.onAtualizacao.subscriptionOptions(undefined, {
      onData: () => void queryClient.invalidateQueries({ queryKey: trpc.painel.agora.queryKey() }),
    }),
  )

  useEffect(() => {
    document.documentElement.dataset.surface = 'tv'
    return () => {
      delete document.documentElement.dataset.surface
    }
  }, [])

  // Agrupamento estável entre ticks do relógio: os cartões memoizados só
  // re-renderizam quando a fotografia da fábrica muda.
  const linhas = useMemo(() => {
    const porLinha = new Map<string, Posto[]>()
    for (const p of agora.data ?? []) {
      const chave = p.linha ?? 'Outros'
      porLinha.set(chave, [...(porLinha.get(chave) ?? []), p])
    }
    return [...porLinha.entries()]
  }, [agora.data])
  const estouradas = (agora.data ?? []).filter((p) => estourou(p.fichaAtual, relogio)).length

  const { cabeTudo, medidorRef } = useCabeNaTela()
  // A cópia de medição só precisa re-renderizar quando a fotografia muda, não a cada segundo.
  const relogioMedidor = useMemo(() => new Date(), [agora.data])
  const emLoop = !cabeTudo && linhas.length > 1
  const { ativa, ciclo } = useLoop(linhas.length, segundos, emLoop)
  const visiveis = cabeTudo ? linhas : linhas.slice(ativa, ativa + 1)
  // Dado antigo na tela com a API fora: avisa, mas não apaga o que já tinha.
  const semConexao = agora.isError

  return (
    <main className="relative flex h-dvh flex-col gap-4 overflow-hidden p-6">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2">
          <MarcaOP className="size-10" />
          <h1 className="text-2xl font-bold">Produção agora</h1>
          {estouradas > 0 && (
            <span
              role="status"
              className="inline-flex items-center gap-2 rounded-full bg-vermelho/20 px-4 py-1 text-lg font-bold text-vermelho"
            >
              <TriangleAlert className="size-5" aria-hidden />
              {estouradas === 1 ? '1 ficha estourada' : `${estouradas} fichas estouradas`}
            </span>
          )}
          {semConexao && (
            <span
              role="status"
              className="inline-flex items-center gap-2 rounded-full bg-amarelo/20 px-4 py-1 text-lg font-bold text-amarelo"
            >
              <CloudOff className="size-5" aria-hidden />
              Sem conexão com o sistema
            </span>
          )}
        </div>
        <div className="flex items-center gap-6">
          {emLoop && (
            <span className="flex items-center gap-3 text-base text-fg-subtle tabular">
              <span aria-live="polite">
                Linha {ativa + 1} de {linhas.length}
              </span>
              <ProgressoDoLoop segundos={segundos} ciclo={ciclo} />
            </span>
          )}
          <time className="text-xl text-fg-muted tabular">
            {relogio.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </time>
        </div>
      </header>

      {agora.isPending && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3 2xl:grid-cols-[repeat(auto-fill,minmax(16rem,1fr))]">
          <EsqueletoCartoes quantidade={6} />
        </div>
      )}
      {agora.data?.length === 0 && (
        <EstadoVazio
          Icone={Factory}
          tamanho="lg"
          titulo="Nenhum posto cadastrado"
          texto="Cadastre linhas, setores e postos em Gestão → Fábrica."
          className="flex-1"
        />
      )}
      {agora.isError && !agora.data && (
        <EstadoVazio
          Icone={CloudOff}
          tom="atencao"
          tamanho="lg"
          titulo="Sem conexão com o sistema"
          texto="O painel volta sozinho quando a rede ou a API responder."
          className="flex-1"
        />
      )}

      {/* Troca de linha com fade curto: sem corte seco na TV. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={cabeTudo ? 'todas' : (visiveis[0]?.[0] ?? 'vazio')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col gap-4"
        >
          {visiveis.map(([linha, postos]) => (
            <SecaoLinha key={linha} linha={linha} postos={postos} relogio={relogio} />
          ))}
        </motion.div>
      </AnimatePresence>

      {/* Cópia invisível com TODAS as linhas: mede se cabe sem loop. */}
      <div
        ref={medidorRef}
        aria-hidden
        className="pointer-events-none invisible absolute inset-x-6 top-6 flex flex-col gap-4"
      >
        <div className="h-[2.5rem]" />
        {linhas.map(([linha, postos]) => (
          <SecaoLinha key={linha} linha={linha} postos={postos} relogio={relogioMedidor} />
        ))}
      </div>
    </main>
  )
}

/** Barrinha que enche no tempo da linha: avisa quando a tela vai trocar. */
function ProgressoDoLoop({ segundos, ciclo }: { segundos: number; ciclo: number }) {
  return (
    <span aria-hidden className="block h-1.5 w-24 overflow-hidden rounded-full bg-elevated">
      <span
        key={`${ciclo}-${segundos}`}
        className="block h-full origin-left rounded-full bg-brand/70"
        style={{ animation: `loop-linha ${segundos}s linear forwards` }}
      />
    </span>
  )
}

/** Mede a cópia invisível contra a altura da janela. */
function useCabeNaTela() {
  const medidorRef = useRef<HTMLDivElement>(null)
  const [cabeTudo, setCabeTudo] = useState(true)
  useLayoutEffect(() => {
    const el = medidorRef.current
    if (!el) return
    const medir = () => {
      const folga = 48 // padding vertical do main
      setCabeTudo(el.offsetHeight + folga <= window.innerHeight)
    }
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    window.addEventListener('resize', medir)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', medir)
    }
  }, [])
  return { cabeTudo, medidorRef }
}

/** Índice da linha ativa e o nº do ciclo (reinicia a barrinha), avançando a cada `segundos` enquanto `ativo`. */
function useLoop(total: number, segundos: number, ativo: boolean) {
  const [estado, setEstado] = useState({ indice: 0, ciclo: 0 })
  useEffect(() => {
    if (!ativo || total <= 1) return
    const id = setInterval(
      () => setEstado((e) => ({ indice: (e.indice + 1) % total, ciclo: e.ciclo + 1 })),
      segundos * 1000,
    )
    return () => clearInterval(id)
  }, [ativo, total, segundos])
  return { ativa: total === 0 ? 0 : estado.indice % total, ciclo: estado.ciclo }
}

const SecaoLinha = memo(function SecaoLinha({
  linha,
  postos,
  relogio,
}: {
  linha: string
  postos: Posto[]
  relogio: Date
}) {
  // Fila por setor (RF-I01): cada setor aparece uma vez, mesmo com vários postos.
  const setores = new Map<string, number>()
  for (const p of postos) setores.set(p.setor, p.fichasNaFilaDoSetor)

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="text-base font-semibold tracking-widest text-fg-muted uppercase">{linha}</h2>
        <ul className="flex flex-wrap gap-2" aria-label={`Fila por setor · ${linha}`}>
          {[...setores.entries()].map(([setor, fila]) => (
            <li
              key={setor}
              className={cn(
                'rounded-full border px-3 py-0.5 text-sm tabular',
                fila > 0 ? 'border-brand/40 bg-brand/10 text-fg' : 'border-border text-fg-subtle',
              )}
            >
              {setor} · {fila} na fila
            </li>
          ))}
        </ul>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3 2xl:grid-cols-[repeat(auto-fill,minmax(16rem,1fr))]">
        {postos.map((p) => (
          <CartaoPosto key={p.id} posto={p} relogio={relogio} />
        ))}
      </div>
    </section>
  )
})

/** Cartão por posto, legível a 3 m: nome e tempo grandes, código discreto, estado pela borda + texto. */
function CartaoPosto({ posto: p, relogio }: { posto: Posto; relogio: Date }) {
  const ficha = p.fichaAtual
  const decorrido = ficha ? decorridoSeg(ficha, relogio) : 0
  const passou = ficha ? decorrido - ficha.tempoPrevistoSeg : 0
  const estourada = estourou(p.fichaAtual, relogio)
  const cor = !ficha
    ? 'border-border'
    : ficha.estado === 'PAUSADA'
      ? 'border-amarelo'
      : estourada
        ? 'border-vermelho'
        : 'border-verde'
  return (
    <article
      aria-label={`${p.nome}: ${
        !ficha
          ? 'livre'
          : ficha.estado === 'PAUSADA'
            ? 'pausado'
            : estourada
              ? 'estourado'
              : 'produzindo'
      }`}
      className={cn(
        'flex flex-col gap-1 card border-l-4 px-4 py-2.5',
        cor,
        estourada && 'animate-pulse bg-vermelho/10 [animation-duration:2s]',
      )}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="truncate text-lg font-semibold">{p.nome}</h3>
        <span className="shrink-0 font-mono text-[0.65rem] text-fg-subtle">{p.codigo}</span>
      </header>
      {/* Só chega com o opt-in da empresa (cláusula 8.4); por padrão a TV não mostra pessoas. */}
      {p.operador && <p className="truncate text-sm text-fg-muted">{p.operador.nome}</p>}
      {ficha ? (
        <>
          <p className="truncate text-base text-fg-muted">
            #{ficha.ficha.itemPedido.pedido.numero} · {ficha.ficha.itemPedido.produto.nome}
          </p>
          {/* Uma linha só: estourada mostra quanto passou no lugar do previsto. */}
          <p
            className={cn(
              'flex items-baseline gap-2 text-3xl leading-tight font-bold tabular',
              ficha.estado === 'PAUSADA' ? 'text-amarelo' : estourada && 'text-vermelho',
            )}
          >
            {ficha.estado === 'PAUSADA' ? 'Pausada' : formatarDuracao(decorrido)}
            {estourada ? (
              <span className="inline-flex items-center gap-1 text-base font-bold">
                <TriangleAlert className="size-4 self-center" aria-hidden />+
                {formatarDuracao(passou)}
              </span>
            ) : (
              <span className="text-base font-normal text-fg-subtle">
                / {formatarDuracao(ficha.tempoPrevistoSeg)}
              </span>
            )}
          </p>
        </>
      ) : (
        <p className="text-base text-fg-subtle">
          {p.ocupado ? 'Livre' : 'Sem operador'} · {p.fichasNaFilaDoSetor} na fila
        </p>
      )}
    </article>
  )
}
